// Lightweight in-app product analytics. Fire-and-forget; never throws into UI.
// Mirrors the pattern used by ctaTracking.ts.
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "sorvaine_product_session_id";
const HEARTBEAT_KEY = "sorvaine_product_session_last_beat";
const HEARTBEAT_MS = 30 * 60 * 1000; // 30 minutes

function newId(): string {
  return (
    crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  );
}

export function getProductSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return newId();
  }
}

export type EventName =
  | "app_session_start"
  | "app_session_heartbeat"
  | "feature_opened"
  | "proposal.stage_changed"
  | "proposal.exported"
  | "section.completed"
  | (string & {}); // forward-compat: allow ad-hoc names

/**
 * Record an in-app product event. Silently swallows failures.
 * Only fires when a user is signed in (RLS requires user_id = auth.uid()).
 */
export async function trackEvent(
  eventName: EventName,
  properties: Record<string, unknown> = {},
): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getUser();
    const user = data?.user;
    if (!user) return;

    let organizationId: string | null = null;
    try {
      const stored = localStorage.getItem("sorvaine_active_org_id");
      if (stored && /^[0-9a-f-]{36}$/i.test(stored)) organizationId = stored;
    } catch {
      /* ignore */
    }

    await supabase.from("product_events").insert({
      user_id: user.id,
      organization_id: organizationId,
      event_name: String(eventName).slice(0, 80),
      properties: properties as never,
      session_id: getProductSessionId(),
      page_path: window.location.pathname.slice(0, 500),
    });
  } catch {
    // analytics must never break UX
  }
}

/**
 * React hook: fire `feature_opened` once per feature per browser session.
 * Uses a module-level Set so remounts (tab switches, route re-renders)
 * don't inflate the count. Resets when the tab closes.
 */
const firedFeatures = new Set<string>();

export function useFeatureOpened(feature: string, extra: Record<string, unknown> = {}): void {
  useEffect(() => {
    if (firedFeatures.has(feature)) return;
    firedFeatures.add(feature);
    void trackEvent("feature_opened", { feature, ...extra });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);
}

/**
 * Start a heartbeat that records `app_session_start` on auth and re-fires
 * every 30 minutes the user remains active. Idempotent across remounts.
 */
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

export function startSessionHeartbeat(): () => void {
  if (typeof window === "undefined" || started) return () => {};
  started = true;

  const beat = (initial: boolean) => {
    try {
      const last = Number(sessionStorage.getItem(HEARTBEAT_KEY) || 0);
      if (!initial && Date.now() - last < HEARTBEAT_MS) return;
      sessionStorage.setItem(HEARTBEAT_KEY, String(Date.now()));
      void trackEvent(initial ? "app_session_start" : "app_session_heartbeat", {
        referrer: document.referrer || null,
      });
    } catch {
      /* noop */
    }
  };

  beat(true);
  heartbeatTimer = setInterval(() => beat(false), HEARTBEAT_MS);

  const onVisibility = () => {
    if (document.visibilityState === "visible") beat(false);
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    started = false;
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
